import React, { useState, useEffect, useMemo } from 'react'
import { AntModal, AntInput, AntSelect, AntDate, AntCheckbox } from "components/AntComponents";
import { Button } from "antd";
import { Formik } from 'formik';
import {
    investorType,
    cityList,
    worldCities,
    countryList,
    investorStatus,
    priority,
    ticket_size_range,
    whoseContactList,
} from "formFields";
import { investorCrudFormSchema } from "./validations";
import Api from "Api/Api";
import Utils from "Utils";

export function InvestorCrud({ visible, onClose, dynamicFields, data, onSubmit }) {
    const INITIAL_FORM_DATA = {
        username: null,
        primary_email: null,
        secondary_email: null,
        contact_number: null,
        dob: undefined,
        password: null,
        country: null,
        invest_in_countries: [],
        city: null,
        address: null,
        linkedin: null,
        facebook: null,
        twitter: null,
        status: null,
        fund_name: null,
        designation: null,
        primary_contact: false,
        min_ticket_size: null,
        max_ticket_size: null,
        industries: [],
        preferred_stages: [],
        client_segments: [],
        sectors: [],
        no_sectors: [],
        type: [],
        contact_person: null,
        comment_box: null,
        relationship_details: null,
        reminder: null,
        reminder_date: undefined,
        startup_invested_text: null,
        priority: null,
        relationship_manager: null,
        tags: []
    }
    const [isOpen, setIsOpen] = useState(false);
    const [formData, setFormData] = useState(INITIAL_FORM_DATA);

    useEffect(() => {
        if (data) {
            setFormData(data);
        }
    }, [data]);

    useEffect(() => {
        setIsOpen(visible)
    }, [visible]);

    const closeModal = () => {
        setFormData(INITIAL_FORM_DATA);
        onClose();
        setIsOpen(false);
    }

    const processCountries = (data, operation) => {
        if ("invest_in_countries" in data) {
            if (data.invest_in_countries) {
                if (operation === "compress") {
                    data.invest_in_countries = data.invest_in_countries.join(",");
                } else if (operation === "decompress") {
                    data.invest_in_countries = data.invest_in_countries.split(",");
                }
            }
        }
        return data;
    };

    const formatType = (data, operation = "decompress") => {
        if (operation === "decompress") {
            return data.map((record) => {
                return {
                    ...record,
                    type: record.type.split(","),
                };
            });
        } else {
            if ("type" in data) {
                data.type = data.type.join(",");
            }
            return data;
        }
    };

    const investorUpdated = async (payload) => {
        delete payload.user;
        let investorData = payload;
        investorData = processCountries(investorData, "compress");
        investorData = formatType(investorData, "compress");
        const response = await Api.editInvestor(data.id, investorData)
        if (response.status !== 200) {
            Utils.notify(
                Utils.errorMessageGenerator(response.data),
                "warning",
                4000
            );
        } else {
            Utils.notify("Investor updated successfully!", "success", 4000);
        }
    };
    const investorAdded = async (payload) => {
        let investorData = payload;
        investorData = processCountries(investorData, "compress");
        investorData = formatType(investorData, "compress");
        const response = await Api.createInvestor(investorData)
        if (response.status !== 200) {
            Utils.notify(
                Utils.errorMessageGenerator(response.data),
                "warning",
                4000
            );

        } else {
            Utils.notify("Investor created successfully!", "success", 4000);
            return response.data;
        }
    };

    const handleSubmit = async (payload, { setSubmitting }) => {
        let newData = payload;
        setSubmitting(true);
        let created = false;
        if (data) {
            await investorUpdated(payload);
        } else {
            created = true;
            newData = await investorAdded(payload);
        }
        onSubmit(newData, created);
        setSubmitting(false);
        closeModal();
    }

    return (
        <AntModal
            title={`${data ? "Edit" : "Create"} Investor`}
            isOpen={isOpen}
            onClose={closeModal}
            options={{
                width: "40%"
            }}
        >
            <Formik
                initialValues={formData}
                enableReinitialize
                validationSchema={investorCrudFormSchema}
                onSubmit={handleSubmit}
            >
                {({
                    values,
                    errors,
                    touched,
                    handleSubmit,
                    setFieldValue,
                }) => {
                    const cityOptions = values.country === "India" ? cityList : (worldCities[values.country] || []);
                    return (
                    <form onSubmit={handleSubmit}>
                        <h4>Personal Details</h4>
                        <hr />
                        <div className="grid grid-cols-2 gap-10 mb-2">
                            <AntInput
                                value={values.username}
                                setFieldValue={setFieldValue}
                                fieldName={"username"}
                                label={"name"}
                                errors={errors}
                                touched={touched}
                                isRequired
                            />
                            <AntInput
                                value={values.primary_email}
                                setFieldValue={setFieldValue}
                                fieldName={"primary_email"}
                                errors={errors}
                                touched={touched}
                                isRequired
                            />
                            <AntInput
                                value={values.secondary_email}
                                setFieldValue={setFieldValue}
                                fieldName={"secondary_email"}
                                errors={errors}
                                touched={touched}
                            />
                            <AntInput
                                value={values.contact_number}
                                setFieldValue={setFieldValue}
                                fieldName={"contact_number"}
                                errors={errors}
                                touched={touched}
                            />
                            <AntDate
                                value={values.dob}
                                setFieldValue={setFieldValue}
                                fieldName={"dob"}
                                label={"DOB"}
                                errors={errors}
                                touched={touched}
                            />
                            {!data && <AntInput
                                value={values.password}
                                setFieldValue={setFieldValue}
                                fieldName={"password"}
                                errors={errors}
                                touched={touched}
                                isRequired
                            />}

                            <AntSelect
                                value={values.country}
                                dataSource={countryList}
                                setFieldValue={setFieldValue}
                                fieldName="country"
                                errors={errors}
                                touched={touched}
                                options={{
                                    showSearch: true
                                }}
                                onChange={() => setFieldValue('city', null)}
                                isRequired
                            />
                            <AntSelect
                                value={values.invest_in_countries.length ? values.invest_in_countries : []}
                                dataSource={countryList}
                                setFieldValue={setFieldValue}
                                fieldName="invest_in_countries"
                                errors={errors}
                                touched={touched}
                                options={{
                                    showSearch: true,
                                    mode: 'multiple',
                                }}
                            />
                            <AntSelect
                                value={values.city}
                                dataSource={cityOptions}
                                setFieldValue={setFieldValue}
                                fieldName="city"
                                errors={errors}
                                touched={touched}
                                options={{
                                    showSearch: true
                                }}
                                isRequired
                            />
                            <AntInput
                                value={values.address}
                                setFieldValue={setFieldValue}
                                fieldName={"address"}
                                errors={errors}
                                touched={touched}
                            />
                            <AntInput
                                value={values.linkedin}
                                setFieldValue={setFieldValue}
                                fieldName={"linkedin"}
                                errors={errors}
                                touched={touched}
                                isRequired
                            />
                            <AntInput
                                value={values.twitter}
                                setFieldValue={setFieldValue}
                                fieldName={"twitter"}
                                errors={errors}
                                touched={touched}
                            />
                            <AntInput
                                value={values.facebook}
                                setFieldValue={setFieldValue}
                                fieldName={"facebook"}
                                errors={errors}
                                touched={touched}
                            />

                        </div>
                        <h4 className="mt-3">Professional Details</h4>
                        <hr />
                        <div className="grid grid-cols-2 gap-10 mb-2">
                            <AntSelect
                                value={values.status}
                                dataSource={investorStatus || []}
                                setFieldValue={setFieldValue}
                                fieldName="status"
                                errors={errors}
                                touched={touched}
                                isRequired
                            />
                            <AntInput
                                value={values.fund_name}
                                setFieldValue={setFieldValue}
                                fieldName={"fund_name"}
                                errors={errors}
                                touched={touched}
                                isRequired
                            />
                            <AntInput
                                value={values.website}
                                setFieldValue={setFieldValue}
                                fieldName={"website"}
                                errors={errors}
                                touched={touched}
                            />
                            <AntInput
                                value={values.designation}
                                setFieldValue={setFieldValue}
                                fieldName={"designation"}
                                errors={errors}
                                touched={touched}
                                isRequired
                            />
                            <AntCheckbox
                                value={values.primary_contact}
                                fieldName={"primary_contact"}
                                setFieldValue={setFieldValue}
                            />
                            <div className="grid grid-cols-2 gap-10">
                                <AntSelect
                                    value={values.min_ticket_size}
                                    dataSource={ticket_size_range || []}
                                    setFieldValue={setFieldValue}
                                    fieldName="min_ticket_size"
                                    errors={errors}
                                    touched={touched}
                                    isRequired
                                />
                                <AntSelect
                                    value={values.max_ticket_size}
                                    dataSource={ticket_size_range || []}
                                    setFieldValue={setFieldValue}
                                    fieldName="max_ticket_size"
                                    errors={errors}
                                    touched={touched}
                                    isRequired
                                />
                            </div>

                            <AntSelect
                                value={values.industries}
                                dataSource={dynamicFields.industry_choices || []}
                                setFieldValue={setFieldValue}
                                fieldName="industries"
                                errors={errors}
                                touched={touched}
                                isRequired
                                options={{
                                    mode: 'multiple'
                                }}
                            />
                            <AntSelect
                                value={values.preferred_stages}
                                dataSource={dynamicFields.preferred_stage_choices || []}
                                setFieldValue={setFieldValue}
                                fieldName="preferred_stages"
                                errors={errors}
                                touched={touched}
                                isRequired
                                options={{
                                    mode: 'multiple'
                                }}
                            />
                            <AntSelect
                                value={values.client_segments}
                                dataSource={dynamicFields.client_segment_choices || []}
                                setFieldValue={setFieldValue}
                                fieldName="client_segments"
                                errors={errors}
                                touched={touched}
                                isRequired
                                options={{
                                    mode: 'multiple'
                                }}
                            />
                            <AntSelect
                                value={values.sectors}
                                dataSource={dynamicFields.sector_choices || []}
                                setFieldValue={setFieldValue}
                                fieldName="sectors"
                                errors={errors}
                                touched={touched}
                                options={{
                                    mode: 'multiple'
                                }}
                                isRequired
                            />
                            <AntSelect
                                value={values.no_sectors}
                                dataSource={dynamicFields.sector_choices || []}
                                setFieldValue={setFieldValue}
                                fieldName="no_sectors"
                                errors={errors}
                                touched={touched}
                                options={{
                                    mode: 'multiple'
                                }}
                            />
                            <AntSelect
                                value={typeof (values.type) === 'string' ? values.type.split(',') : values.type}
                                dataSource={investorType}
                                setFieldValue={setFieldValue}
                                fieldName="type"
                                errors={errors}
                                touched={touched}
                                options={{
                                    mode: 'multiple'
                                }}
                                isRequired
                            />
                            <AntSelect
                                value={values.contact_person}
                                dataSource={whoseContactList}
                                setFieldValue={setFieldValue}
                                fieldName="contact_person"
                                label={"Whose contact"}
                                errors={errors}
                                touched={touched}
                                isRequired
                            />
                            <AntSelect
                                value={values.priority}
                                dataSource={priority}
                                setFieldValue={setFieldValue}
                                fieldName="priority"
                                errors={errors}
                                touched={touched}
                                isRequired
                            />
                            <AntSelect
                                value={values.relationship_manager}
                                dataSource={dynamicFields.relationship_managers_choices}
                                setFieldValue={setFieldValue}
                                fieldName="relationship_manager"
                                errors={errors}
                                touched={touched}
                                isRequired
                            />
                            <AntInput
                                value={values.comment_box}
                                setFieldValue={setFieldValue}
                                fieldName={"comment_box"}
                                errors={errors}
                                touched={touched}
                            />
                            <AntInput
                                value={values.reminder}
                                setFieldValue={setFieldValue}
                                fieldName={"reminder"}
                                errors={errors}
                                touched={touched}
                            />
                            <AntDate
                                value={values.reminder_date}
                                setFieldValue={setFieldValue}
                                fieldName={"reminder_date"}
                                errors={errors}
                                touched={touched}
                            />
                            <AntInput
                                value={values.startup_invested_text}
                                setFieldValue={setFieldValue}
                                fieldName={"startup_invested_text"}
                                errors={errors}
                                label={"Startup invested"}
                                touched={touched}
                            />
                            <AntSelect
                                value={values.tags}
                                dataSource={dynamicFields.tag_choices}
                                setFieldValue={setFieldValue}
                                fieldName="tags"
                                errors={errors}
                                touched={touched}
                                options={{
                                    mode: 'multiple',
                                }}
                            />
                            <AntInput
                                value={values.relationship_details}
                                setFieldValue={setFieldValue}
                                fieldName={"relationship_details"}
                                errors={errors}
                                touched={touched}
                            />

                        </div>
                        <Button type="primary" htmlType="submit" className="flex flex-row-reverse">Save</Button>

                    </form>
                    );
                }}
            </Formik>
        </AntModal>
    )
}
